import * as Sentry from "@sentry/react";
import { isCancel } from "axios";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import TagManager from "react-gtm-module";
import App from "./App";
import { configuration } from "./configuration/configuration";
import { isAlgoliaApiError, isAuth0LoginRequired, isFromExtension, matchesRpcMessage } from "./utils/SentryUtils";

import "./i18n";

import "@/assets/css/global-style-overrides.css";
import "./index.css";

if (configuration.gtm) {
    TagManager.initialize({
        gtmId: configuration.gtm.id,
    });
}

if (configuration.sentry) {
    Sentry.init({
        dsn: configuration.sentry.dsn,
        sendDefaultPii: true,
        environment: configuration.sentry.env,
        tracesSampleRate: configuration.sentry.sampleRate,
        integrations: [
            Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
            Sentry.replayIntegration(),
        ],
        enableLogs: configuration.sentry.enableLogs,
        replaysSessionSampleRate: configuration.sentry.replaysSessionSampleRate,
        replaysOnErrorSampleRate: configuration.sentry.replaysOnErrorSampleRate,
        beforeSend(event, hint) {
            const original = hint?.originalException;
            const ex = event.exception?.values?.[0];
            const msg = ex?.value ?? "";
            const type = ex?.type ?? "";

            // Don't pass canceled requests to Sentry
            if (isCancel(original)) return null;

            // Don't pass Algolia ApiErrors to Sentry (these are triggered on dev/stg environments because the Algolia app is not always running there)
            if (isAlgoliaApiError(original)) return null;
            if (type === "ApiError") return null;

            // Don't pass Auth0 login_required errors to Sentry
            if (isAuth0LoginRequired(original)) return null;
            if (/login required/i.test(String(msg)) || /login_required/i.test(String(type))) return null;

            // Don't pass RPC "Object Not Found Matching Id"/Extension errors to Sentry
            if (matchesRpcMessage(event, hint) || isFromExtension(event)) return null;

            return event;
        },
    });
}

createRoot(document.getElementById("root")!).render(
    <StrictMode>
        <App />
    </StrictMode>
);
